import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import api from '../services/api';
import './Login.css';
import './CheckStatus.css';

const STATUS_META = {
    active: { cls: 'ok', title: 'บัญชีพร้อมใช้งาน', desc: 'การเงินเปิดบัญชีให้แล้ว สามารถเข้าสู่ระบบได้ด้วยรหัสผ่านที่ตั้งไว้' },
    slip_ready: { cls: 'ok', title: 'เข้าใช้งานได้', desc: 'เข้าสู่ระบบได้โดยใช้รหัสผ่านเดียวกับระบบ Slip Online' },
    pending: { cls: 'pending', title: 'คำขออยู่ระหว่างดำเนินการ', desc: 'เจ้าหน้าที่การเงินกำลังตรวจสอบคำขอของคุณ กรุณารอการติดต่อกลับ' },
    rejected: { cls: 'error', title: 'คำขอถูกปฏิเสธ', desc: 'คำขอเปิดบัญชีของคุณไม่ผ่านการอนุมัติ' },
    not_found: { cls: 'neutral', title: 'ยังไม่พบบัญชีในระบบ', desc: 'ยังไม่มีบัญชีสำหรับเลขบัตรนี้ กรุณาลงทะเบียนขอเปิดบัญชี' },
    banned: { cls: 'error', title: 'บัญชีถูกระงับการใช้งาน', desc: 'บัญชีนี้ถูกระงับ กรุณาติดต่อเจ้าหน้าที่การเงิน' },
    suspended: { cls: 'error', title: 'บัญชีถูกปิดใช้งาน', desc: 'บัญชีนี้ถูกปิดใช้งาน กรุณาติดต่อเจ้าหน้าที่การเงิน' }
};

function CheckStatus() {
    const [cid, setCid] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { appName } = useBranding();

    // เพิ่มอีเมลเอง (สำหรับคำขอที่ยังไม่มีอีเมล)
    const [emailInput, setEmailInput] = useState('');
    const [codeInput, setCodeInput] = useState('');
    const [emailStep, setEmailStep] = useState('idle'); // 'idle' | 'sent'
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);

        if (!/^\d{13}$/.test(cid)) {
            setError('กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก');
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('/auth/check-status', { cid });
            setResult(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการตรวจสอบ');
        } finally {
            setLoading(false);
        }
    };

    const handleSendCode = async () => {
        const em = emailInput.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
            setEmailMsg({ type: 'error', text: 'กรุณากรอกอีเมลให้ถูกต้อง' });
            return;
        }
        setEmailLoading(true);
        setEmailMsg(null);
        try {
            await api.post('/auth/register/send-code', { email: em });
            setEmailStep('sent');
            setEmailMsg({ type: 'success', text: `ส่งรหัสยืนยันไปที่ ${em} แล้ว (หมดอายุใน 15 นาที)` });
        } catch (err) {
            setEmailMsg({ type: 'error', text: err.response?.data?.error || 'ส่งรหัสไม่สำเร็จ' });
        } finally {
            setEmailLoading(false);
        }
    };

    const handleVerifyAndSave = async () => {
        const em = emailInput.trim().toLowerCase();
        const code = codeInput.replace(/\D/g, '');
        if (!/^\d{6}$/.test(code)) {
            setEmailMsg({ type: 'error', text: 'กรุณากรอกรหัส 6 หลัก' });
            return;
        }
        setEmailLoading(true);
        setEmailMsg(null);
        try {
            await api.post('/auth/register/verify-code', { email: em, code });
            await api.post('/auth/request/add-email', { cid, email: em });
            setEmailMsg({ type: 'success', text: 'เพิ่มและยืนยันอีเมลเรียบร้อยแล้ว ✓' });
            // โหลดสถานะใหม่ (needsEmail จะกลายเป็น false → widget หาย)
            const res = await api.post('/auth/check-status', { cid });
            setResult(res.data);
            setEmailStep('idle');
            setCodeInput('');
            setEmailInput('');
        } catch (err) {
            setEmailMsg({ type: 'error', text: err.response?.data?.error || 'ยืนยัน/บันทึกไม่สำเร็จ' });
        } finally {
            setEmailLoading(false);
        }
    };

    const meta = result ? (STATUS_META[result.status] || STATUS_META.not_found) : null;

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card check-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img
                                src={`${process.env.PUBLIC_URL}/icon-512.png`}
                                alt={appName}
                                className="login-logo-icon"
                            />
                            <h1 className="login-title">{appName}</h1>
                        </div>
                        <p className="login-subtitle">ตรวจสอบสถานะบัญชี</p>
                        <p className="login-hospital">โรงพยาบาลศรีสะเกษ</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <div className="alert alert-error">
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label className="form-label">เลขบัตรประชาชน</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="steam-input"
                                value={cid}
                                onChange={(e) => setCid(e.target.value.replace(/\D/g, '').slice(0, 13))}
                                placeholder="กรอกเลขบัตรประชาชน 13 หลัก"
                                required
                                autoFocus
                            />
                        </div>

                        <button type="submit" className="steam-btn steam-btn-primary login-btn" disabled={loading}>
                            {loading ? (
                                <>
                                    <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                    กำลังตรวจสอบ...
                                </>
                            ) : 'ตรวจสอบสถานะ'}
                        </button>
                    </form>

                    {result && meta && (
                        <div className={`check-result check-${meta.cls}`}>
                            <div className="check-result-head">
                                <span className="check-status-badge">{meta.title}</span>
                            </div>
                            <p className="check-result-desc">{meta.desc}</p>

                            {(result.fullname || result.employee) && (
                                <div className="check-detail">
                                    {result.fullname && (
                                        <div className="check-detail-row">
                                            <span>ชื่อ-นามสกุล</span><strong>{result.fullname}</strong>
                                        </div>
                                    )}
                                    <div className="check-detail-row">
                                        <span>เลขบัตร</span><strong>{result.cid}</strong>
                                    </div>
                                    {result.employee?.emp_code && (
                                        <div className="check-detail-row">
                                            <span>รหัสพนักงาน</span><strong>{result.employee.emp_code}</strong>
                                        </div>
                                    )}
                                    {result.employee?.position && (
                                        <div className="check-detail-row">
                                            <span>ตำแหน่ง</span><strong>{result.employee.position}</strong>
                                        </div>
                                    )}
                                    {result.employee?.department && (
                                        <div className="check-detail-row">
                                            <span>หน่วยงาน</span><strong>{result.employee.department}</strong>
                                        </div>
                                    )}
                                    {result.account?.created_at && (
                                        <div className="check-detail-row">
                                            <span>เปิดบัญชีเมื่อ</span><strong>{formatDate(result.account.created_at)}</strong>
                                        </div>
                                    )}
                                    {result.account?.last_login && (
                                        <div className="check-detail-row">
                                            <span>เข้าระบบล่าสุด</span><strong>{formatDate(result.account.last_login)}</strong>
                                        </div>
                                    )}
                                    {result.status === 'pending' && result.request?.created_at && (
                                        <div className="check-detail-row">
                                            <span>ยื่นคำขอเมื่อ</span><strong>{formatDate(result.request.created_at)}</strong>
                                        </div>
                                    )}
                                    {result.status === 'rejected' && result.request?.admin_note && (
                                        <div className="check-detail-row">
                                            <span>เหตุผล</span><strong>{result.request.admin_note}</strong>
                                        </div>
                                    )}
                                </div>
                            )}

                            {result.needsEmail && (
                                <div style={{ marginTop: 16, padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12 }}>
                                    <p style={{ margin: '0 0 6px', color: '#92400e', fontWeight: 600 }}>📧 เพิ่มอีเมลเพื่อรับการแจ้งเตือน</p>
                                    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#78350f' }}>
                                        คำขอของคุณยังไม่มีอีเมล — กรอกและยืนยันอีเมลของคุณ เพื่อให้เจ้าหน้าที่แจ้งผลและส่งเอกสารทางอีเมลได้
                                    </p>
                                    {emailStep === 'idle' ? (
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <input
                                                type="email"
                                                className="steam-input"
                                                style={{ flex: 1, minWidth: 200 }}
                                                placeholder="กรอกอีเมลของคุณ"
                                                value={emailInput}
                                                onChange={(e) => setEmailInput(e.target.value)}
                                            />
                                            <button type="button" className="steam-btn steam-btn-primary" onClick={handleSendCode} disabled={emailLoading}>
                                                {emailLoading ? 'กำลังส่ง...' : 'ส่งรหัสยืนยัน'}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <p style={{ margin: '0 0 8px', fontSize: 13 }}>กรอกรหัส 6 หลักที่ส่งไปที่ <strong>{emailInput}</strong></p>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                <input
                                                    inputMode="numeric"
                                                    className="steam-input"
                                                    style={{ flex: 1, minWidth: 140 }}
                                                    placeholder="รหัส 6 หลัก"
                                                    value={codeInput}
                                                    onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                />
                                                <button type="button" className="steam-btn steam-btn-primary" onClick={handleVerifyAndSave} disabled={emailLoading}>
                                                    {emailLoading ? 'กำลังบันทึก...' : 'ยืนยันและบันทึก'}
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setEmailStep('idle'); setCodeInput(''); setEmailMsg(null); }}
                                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, marginTop: 8, textDecoration: 'underline', padding: 0 }}
                                            >
                                                เปลี่ยนอีเมล / ขอรหัสใหม่
                                            </button>
                                        </>
                                    )}
                                    {emailMsg && (
                                        <div style={{ marginTop: 10, fontSize: 13, color: emailMsg.type === 'error' ? '#dc2626' : '#16a34a' }}>{emailMsg.text}</div>
                                    )}
                                </div>
                            )}

                            <div className="check-actions">
                                {result.canLogin ? (
                                    <Link to="/login" className="steam-btn steam-btn-primary">เข้าสู่ระบบ</Link>
                                ) : (result.status === 'not_found' || result.status === 'rejected') ? (
                                    <Link to="/register" className="steam-btn steam-btn-primary">ลงทะเบียนขอเปิดบัญชี</Link>
                                ) : null}
                            </div>
                        </div>
                    )}

                    <div className="login-footer">
                        <p>
                            <Link to="/login" className="register-link">เข้าสู่ระบบ</Link>
                            {'  •  '}
                            <Link to="/register" className="register-link">ลงทะเบียนขอเปิดบัญชี</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CheckStatus;
